// Client-side scrub for event props. The rule for the whole flywheel: no census
// values and no direct identifiers, ever. This is the first of two gates (the
// record_app_events RPC re-strips identifier-shaped keys server-side).
//
// What survives: short scalars describing WHAT happened (setting keys, enum
// choices, counts, booleans, durations). What is removed: anything keyed like an
// identifier or free-form text field. Pattern scrubbing remains as defense in
// depth for controlled categorical strings, but analytics must never depend on
// recognizing whether arbitrary prose contains a person's name.

const MAX_KEYS = 120
const MAX_STRING_LENGTH = 280
const MAX_ARRAY_ITEMS = 80

// Keep in sync with public.app_events_key_is_identifier in the SQL migration.
const IDENTIFIER_KEY_RE =
  /(^|_)(name|first_?name|last_?name|full_?name|ssn|email|dob|birth|birthdate|salary|wage|compensation|comp|employee_?id|emp_?id|phone|address)($|_)/i

// Questions, prompts, labels, notes, and similar values are user-controlled and
// therefore unsafe even when their key is not itself a conventional identifier.
// Keep in sync with public.app_events_key_is_identifier.
const FREE_TEXT_KEY_RE =
  /(^|_)(question|answer|prompt|message|text|note|description|title|label|plan_?class|company(?:_?name)?|firm(?:_?name)?|client(?:_?name)?|prepared_?by)($|_)/i

const EMAIL_RE = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi
// 7+ consecutive digits (optionally separated) -> SSN / phone / account shaped.
const LONG_DIGITS_RE = /\b(?:\d[\s-]?){7,}\b/g

function scrubString(value) {
  let out = String(value)
    .replace(EMAIL_RE, '[redacted-email]')
    .replace(LONG_DIGITS_RE, '[redacted-number]')
  if (out.length > MAX_STRING_LENGTH) out = `${out.slice(0, MAX_STRING_LENGTH)}…`
  return out
}

function scrubValue(value, depth, options) {
  if (value == null) return value
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value === 'boolean') return value
  if (typeof value === 'string') return scrubString(value)
  if (Array.isArray(value)) {
    if (depth >= 2) return undefined
    return value.slice(0, MAX_ARRAY_ITEMS).map((item) => scrubValue(item, depth + 1, options))
  }
  if (typeof value === 'object') {
    if (depth >= 2) return undefined
    return scrubObject(value, depth + 1, options)
  }
  return undefined
}

function scrubObject(props, depth, options = {}) {
  const out = {}
  let count = 0
  for (const [key, raw] of Object.entries(props || {})) {
    if (count >= MAX_KEYS) break
    const allowedQuestion = depth === 0 && key === 'question' && options.allowDeidentifiedQuestion
    if (IDENTIFIER_KEY_RE.test(key) || (FREE_TEXT_KEY_RE.test(key) && !allowedQuestion)) continue
    const scrubbed = scrubValue(raw, depth, options)
    if (scrubbed === undefined) continue
    out[key] = scrubbed
    count += 1
  }
  return out
}

// Returns a plain object safe to send. Never throws.
export function sanitizeProps(props, options = {}) {
  if (!props || typeof props !== 'object' || Array.isArray(props)) return {}
  try {
    return scrubObject(props, 0, options)
  } catch {
    return {}
  }
}

export const __testables = { scrubString, IDENTIFIER_KEY_RE, FREE_TEXT_KEY_RE }
